import { useCallback } from "react";
import toast from "react-hot-toast";
import {
  getNotificationPermission,
  isBrowserNotificationSupported,
  requestNotificationPermission,
} from "@/utils/browser-notification-utils";
import { useBrowserNotificationSettings } from "@/pages/settings/hooks/useBrowserNotificationSettings";
import { useWatchlistAlertMode } from "@/pages/settings/hooks/useWatchlistAlertMode";

// Shared id so repeated nudges (e.g. ticking Sound then Pop-Up) coalesce into one.
const ALERT_MODE_HINT_ID = "watchlist-alert-mode-hint";

/**
 * Callbacks to run when a user switches a watchlist Sound / Pop-Up toggle ON.
 * Call them directly from the click — Pop-Up may request OS permission, which
 * browsers only allow from a user gesture.
 *
 * Ticking a box alone never produced an alert: both channels also need a
 * Watchlist Alert Mode, and Pop-Up additionally needs OS permission + the global
 * master toggle. These bring the pipeline live where they can, and otherwise
 * surface the missing gate via a toast instead of failing silently. The
 * watchlist's own flags stay owned by the editor.
 */
export function useEnsureWatchlistAlerts() {
  const { settings, updateSettings } = useBrowserNotificationSettings();
  const { watchlistAlertMode } = useWatchlistAlertMode();

  // Returns true when it showed the "alert mode is off" hint (the dominant gate).
  const alertModeHint = useCallback(
    (channel: string) => {
      if (watchlistAlertMode === "off") {
        toast(
          `${channel} enabled — also set a Watchlist Alert Mode in Settings → Notifications so alerts trigger.`,
          { id: ALERT_MODE_HINT_ID }
        );
        return true;
      }
      return false;
    },
    [watchlistAlertMode]
  );

  const ensureSound = useCallback(() => {
    alertModeHint("Sound");
  }, [alertModeHint]);

  const ensurePopup = useCallback(async () => {
    if (!isBrowserNotificationSupported()) {
      toast.error("This browser doesn't support notifications.");
      return;
    }

    let permission = getNotificationPermission();
    if (permission === "default") {
      permission = await requestNotificationPermission();
    }

    if (permission !== "granted") {
      toast.error(
        "Notifications are blocked in your browser. Enable them in Settings → Notifications to receive pop-ups."
      );
      return;
    }

    const masterWasOff = !settings.enabled;
    if (masterWasOff) {
      updateSettings({ enabled: true });
    }

    // If the alert mode still gates everything, that's the message to show;
    // otherwise confirm the newly-enabled master toggle.
    const hinted = alertModeHint("Pop-up");
    if (!hinted && masterWasOff) {
      toast.success("Browser pop-ups enabled.");
    }
  }, [settings.enabled, updateSettings, alertModeHint]);

  return { ensureSound, ensurePopup };
}
